import type { RouteRecordRaw } from 'vue-router'

import { SCORECARD_VIEW_PERMISSION } from './composables/useScorecardRbac'
import { registerScorecardMessages } from './i18n'

import { useLocaleStore } from '@/shared/i18n'

/**
 * The KPI Tracking surface. The URL is `/kpi-tracking` and the name is `KPITracking` because
 * that is the ONLY route v3 renders `Scorecards.vue` from (`router/routes/app.js:323–327`);
 * `?report_id=55` also resolves here through v3's report→route map (`reportStore.ts:244`).
 *
 * ⚠ Contract 10 §11 sketches `path:'scorecards'`, `name:'Scorecards'`, `permission:'sysadmin'`.
 * All three are wrong against the code:
 *   • there is NO `/scorecards` route anywhere in v3's router — `git grep "name: *'Scorecards'"`
 *     over `origin/main` returns zero hits, so §11's "coexists with legacy /kpi-tracking" describes
 *     a pairing that never shipped. No alias is added here: a second path would be a URL nobody
 *     can have bookmarked, and it would split analytics and the page title across two names.
 *   • `permission:'sysadmin'` was a dark-launch gate for that never-shipped second surface. This
 *     route IS the live surface, so sysadmin-gating it would delete the page for every real user
 *     — a regression dressed as a rollout.
 *
 * The gate is `kpi_realized_values`, the key v3 declares on the sidebar leaf
 * (`DefaultLayout.vue:250–255`). v3 leaves the route itself ungated, so the page is reachable by
 * anyone who types the URL, through the navbar shortcut and via the onboarding checklist — that
 * is a defect, not a design, and the standing rule is to port the design. `permissionGuard`
 * (05) evaluates this key and redirects a denied actor home; the page repeats the check as a
 * `forbidden` state so a stale or bypassed navigation still discloses nothing.
 */
export const SCORECARDS_ROUTE = { name: 'KPITracking' } as const

export const scorecardRoutes: RouteRecordRaw[] = [
  {
    path: 'kpi-tracking',
    name: SCORECARDS_ROUTE.name,
    component: () => import('./components/pages/ScorecardsPage.vue'),
    meta: {
      requiresAuth: true,
      permission: SCORECARD_VIEW_PERMISSION,
      navKey: 'kpiTracking',
      titleKey: 'scorecards.page.title',
    },
    beforeEnter: () => registerScorecardMessages(useLocaleStore().locale),
  },
]
